import React, { useState } from 'react';
import { Table, Button, Card, Tag, Space, Typography, message, Badge, Tooltip, Statistic } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { EditOutlined, PlusOutlined, EyeOutlined, SortAscendingOutlined, BookOutlined } from '@ant-design/icons';
import { getCourse } from './api';
import LessonForm from './LessonForm';

const { Title } = Typography;

interface CourseLessonsProps {
  courseId: number;
  onBack: () => void;
}

interface Lesson {
  id: number;
  title: string;
  content?: string;
  sort_order: number;
  examType: 'test' | 'open';
  createdAt: string;
  updatedAt: string;
}

const CourseLessons: React.FC<CourseLessonsProps> = ({ courseId, onBack }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState<number | undefined>();

  const { data: course, isLoading, error, refetch } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => getCourse(courseId),
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    { 
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <SortAscendingOutlined style={{ color: '#1976d2' }} />
          <span>Порядок</span>
        </div>
      ), 
      dataIndex: "sort_order", 
      key: "sort_order", 
      width: 100,
      sorter: (a: Lesson, b: Lesson) => a.sort_order - b.sort_order,
      defaultSortOrder: 'ascend' as const,
      render: (sortOrder: number, record: Lesson, index: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Badge 
            count={sortOrder} 
            style={{ 
              backgroundColor: '#1976d2',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          />
          <div style={{ 
            fontSize: '12px', 
            color: '#666',
            fontWeight: '500'
          }}>
            #{index + 1}
          </div>
        </div>
      ),
    },
    { 
      title: "Название", 
      dataIndex: "title", 
      key: "title",
      ellipsis: true,
      render: (text: string, record: Lesson) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            maxWidth: 300, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            fontWeight: '500'
          }}>
            {text}
          </div>
        </div>
      )
    },
    {
      title: "Тип экзамена",
      dataIndex: "examType",
      key: "examType",
      width: 150,
      render: (examType: string) => {
        return examType === "test" ? (
          <Tag color="blue" style={{ fontWeight: '500' }}>Тест</Tag>
        ) : (
          <Tag color="green" style={{ fontWeight: '500' }}>Открытый вопрос</Tag>
        );
      },
    },
    { 
      title: "Создан", 
      dataIndex: "createdAt", 
      key: "createdAt",
      width: 150,
      render: (date: string) => formatDate(date)
    },
    { 
      title: "Обновлён", 
      dataIndex: "updatedAt", 
      key: "updatedAt",
      width: 150,
      render: (date: string) => formatDate(date)
    },
    {
      title: "Действия",
      key: "actions",
      width: 200,
      render: (_: any, record: Lesson) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEditLesson(record.id)}
            size="small"
          >
            Редактировать
          </Button>
        </Space>
      ),
    },
  ];

  const handleEditLesson = (lessonId: number) => {
    setEditingLessonId(lessonId);
    setShowForm(true);
  };

  const handleCreateLesson = () => {
    setEditingLessonId(undefined);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingLessonId(undefined);
    refetch();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingLessonId(undefined);
  };

  if (isLoading) return <div>Загрузка...</div>;
  if (error) return <div>Ошибка: {(error as Error).message}</div>;

  const lessons = course?.lessons || [];
  const sortedLessons = [...lessons].sort((a, b) => a.sort_order - b.sort_order);
  
  // Статистика
  const testLessons = lessons.filter((l: any) => l.examType === 'test').length;
  const openLessons = lessons.filter((l: any) => l.examType === 'open').length;

  return (
    <div>
      {showForm ? (
        <LessonForm
          lessonId={editingLessonId}
          courseId={courseId}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      ) : (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div>
              <Button 
                type="text" 
                icon={<EyeOutlined />} 
                onClick={onBack}
                style={{ padding: 0, height: 'auto', marginBottom: 8 }}
              >
                ← Назад к курсам
              </Button>
              <Title level={3} style={{ margin: 0, color: "#1976d2" }}>
                Уроки курса: {course?.title}
              </Title>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateLesson}
              style={{
                background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                border: "none",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              }}
            >
              Добавить урок
            </Button>
          </div>

          {lessons.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666',
              background: '#f9f9f9',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>
                В этом курсе пока нет уроков
              </div>
              <div style={{ fontSize: '14px', marginBottom: '16px' }}>
                Создайте первый урок, чтобы начать обучение
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateLesson}
              >
                Создать первый урок
              </Button>
            </div>
          ) : (
            <div>
              {/* Статистика курса */}
              <div style={{ 
                marginBottom: '16px', 
                padding: '16px', 
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '16px' 
                }}>
                  <Statistic 
                    title="Всего уроков" 
                    value={lessons.length} 
                    prefix={<BookOutlined />}
                    valueStyle={{ color: '#1976d2' }}
                  />
                  <Statistic 
                    title="Тесты" 
                    value={testLessons} 
                    prefix={<Tag color="blue">Тест</Tag>}
                  />
                  <Statistic 
                    title="Открытые вопросы" 
                    value={openLessons} 
                    prefix={<Tag color="green">Открытый</Tag>}
                  />
                  <Statistic 
                    title="AI-ассистент" 
                    value={course?.aiAssistant === 'claude' ? 'Claude' : 'GPT'} 
                    prefix={
                      course?.aiAssistant === 'claude' ? 
                        <Tag color="purple">Claude</Tag> : 
                        <Tag color="blue">GPT</Tag>
                    }
                  />
                </div>
              </div>

              {/* Информация о сортировке */}
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px 16px', 
                background: 'linear-gradient(135deg, #f0f8ff 0%, #e3f2fd 100%)', 
                borderRadius: '8px',
                border: '1px solid #d6e4ff'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  fontSize: '14px', 
                  color: '#1976d2', 
                  fontWeight: '500' 
                }}>
                  <SortAscendingOutlined style={{ fontSize: '16px' }} />
                  <span>🎯 Сортировка: по порядковому номеру (возрастающая)</span>
                  <span>•</span>
                  <span>📊 Первый урок выделен зеленым цветом</span>
                  <span>•</span>
                  <span>🔄 Можно изменить порядок в форме редактирования</span>
                </div>
              </div>

              {/* Показываем порядок уроков в виде списка */}
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px 16px', 
                background: '#fafafa', 
                borderRadius: '6px',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
                  📋 Последовательность уроков:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {sortedLessons.map((lesson, index) => (
                    <Tooltip key={lesson.id} title={`Порядковый номер: ${lesson.sort_order}`}>
                      <div style={{
                        padding: '4px 8px',
                        background: index === 0 ? '#e8f5e8' : '#f0f0f0',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: index === 0 ? '#2e7d32' : '#666',
                        border: index === 0 ? '1px solid #4caf50' : '1px solid #ddd',
                        cursor: 'pointer'
                      }}>
                        {index + 1}. {lesson.title.length > 20 ? lesson.title.substring(0, 20) + '...' : lesson.title}
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </div>

              <Table
                columns={columns}
                dataSource={sortedLessons}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1000 }}
                rowClassName={(record, index) => 
                  index === 0 ? 'first-lesson-row' : ''
                }
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default CourseLessons; 